import React, { useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { ConfigProvider } from 'antd'
import enUS from 'antd/locale/en_US'
import AppRoutes from '@/routes'
import { MasterDataProvider } from '@/context/MasterDataContext'
import { leadStore } from '@/pages/Lead/leadStore'
import { refreshAllStores } from '@/utils/createStore'
import { authStore } from '@/context/auth'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import '@/utils/notify.css'
import './App.css'

const App: React.FC = () => {
  // Hydrate API-backed stores once at startup — but only when a session
  // already exists (e.g. a hard refresh while logged in). Reads require a
  // valid token, so firing this before login always 401s and is pure waste
  // — worse, each store's `loaded` flag (used to gate loading spinners, see
  // createStore.ts) would latch true off that doomed attempt, so pages would
  // stop showing a spinner before LoginPage's post-login refresh (the one
  // that actually succeeds) has landed. LoginPage.tsx calls refreshAllStores()
  // /leadStore.refresh() itself right after a successful login, so the
  // not-yet-logged-in case is still covered.
  useEffect(() => {
    if (authStore.isLoggedIn()) {
      leadStore.refresh()
      refreshAllStores()
    }
  }, [])

  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        token: {
          colorPrimary:    '#001A4D',
          colorSuccess:    '#059669',
          colorWarning:    '#F59E0B',
          colorError:      '#DC2626',
          colorInfo:       '#2563EB',
          borderRadius:    10,
          fontFamily:      "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          fontSize:        14,
          colorBgBase:     '#F7F8FB',
          colorBgContainer:'#FFFFFF',
          colorBorder:     '#E4E8F0',
          colorTextBase:   '#0B1426',
          colorTextSecondary: '#4A5568',
          boxShadow:       '0 1px 2px rgba(11,20,38,0.04), 0 1px 1px rgba(11,20,38,0.03)',
          boxShadowSecondary: '0 4px 12px rgba(11,20,38,0.06), 0 2px 4px rgba(11,20,38,0.04)',
        },
        components: {
          Table: {
            headerBg:       '#F1F4F9',
            headerColor:    '#4A5568',
            rowHoverBg:     '#EFF4FF',
            borderColor:    '#E4E8F0',
            fontSize:       13,
          },
          Card: {
            headerFontSize: 14,
            headerFontSizeSM: 13,
          },
          Button: {
            borderRadius:   6,
            fontWeight:     600,
          },
          Tag: {
            borderRadius:   100,
            fontSize:       11,
          },
          Tabs: {
            inkBarColor:    '#001A4D',
            itemActiveColor:'#001A4D',
            itemSelectedColor: '#001A4D',
          },
        },
      }}
    >
      <BrowserRouter>
        <MasterDataProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </MasterDataProvider>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default App
